// @flow
import React from 'react';
import injectSheet from 'react-jss';
import { SketchPicker } from 'react-color';
import classnames from 'classnames';
import { BACKGROUND_PICKER_CONTENT_TYPE, BACKGROUND_IMAGE_POSITION } from 'shared/constants';
import Dropdown from './Dropdown';
import Checkbox from './Checkbox';
import S3AssetBrowser from '~/pages/components/S3AssetBrowser';
import { S, DARK } from '~/app/ui/tokens';

type Props = {
  classes: Object,
  value: {
    use_background_color: boolean,
    color: string,
    content_type: string,
    content: string,
    tablet_content: string,
    position: string,
    background_to_image_mobile: boolean,
    mobile_content: string
  },
  options: string[],
  onChange: Function,
  error: string
}

const BackgroundPicker = (props: Props) => {
  const {
    classes: c, value, onChange
  } = props;
  const draft = { ...value };

  return (
    <div className={c.Container}>
      <div className={c.itemContainer}>
        {'Use Background Color'}
        <Checkbox
          value={value.use_background_color}
          onChange={use_background_color => onChange({ ...draft, use_background_color })}
        />
      </div>
      {
        value.use_background_color && (
          <div className={c.itemContainer}>
            <div>Background Color</div>
            <SketchPicker
              color={value.color || '#000000'}
              onChange={color => onChange({ ...draft, color: color.hex })}
            />
          </div>
        )
      }
      <div className={c.itemContainer}>
        {'Content Type'}
        <Dropdown
          value={value.content_type}
          options={BACKGROUND_PICKER_CONTENT_TYPE}
          onChange={newVal => onChange({ ...draft, content_type: newVal, content: '' })}
        />
      </div>
      {
        value.content_type !== 'None' && (
          <div className={classnames(c.itemContainer, c.textInputContainer)}>
            <div>Content URL</div>
            <S3AssetBrowser
              key={`AssetBrowser-${value.content_type}`}
              value={value.content}
              onChange={assetUrl => onChange({ ...draft, content: assetUrl })}
            />
          </div>
        )
      }
      {
        value.content_type === 'Image' && (
          <div className={classnames(c.itemContainer, c.textInputContainer)}>
            <div>Tablet Image Content URL</div>
            <S3AssetBrowser
              key={`AssetBrowser-tablet-${value.content_type}`}
              value={value.tablet_content}
              onChange={assetUrl => onChange({ ...draft, tablet_content: assetUrl })}
            />
          </div>
        )
      }
      {
        value.content_type === 'Image' && (
          <div className={c.itemContainer}>
            <div>Position</div>
            <Dropdown
              value={value.position}
              options={BACKGROUND_IMAGE_POSITION}
              onChange={newVal => onChange({ ...draft, position: newVal })}
            />
          </div>
        )
      }
      {
        value.content_type === 'Image' && (
          <div className={c.itemContainer}>
            {'Show background as standalone image on mobile'}
            <Checkbox
              value={value.background_to_image_mobile}
              onChange={background_to_image_mobile => onChange({ ...draft, background_to_image_mobile })}
            />
          </div>
        )
      }
      {
        value.content_type !== 'None' && (
          <div className={classnames(c.itemContainer, c.textInputContainer)}>
            <div>Mobile Content URL</div>
            <S3AssetBrowser
              key={`AssetBrowser-mobile-${value.content_type}`}
              value={value.mobile_content}
              onChange={assetUrl => onChange({ ...draft, mobile_content: assetUrl })}
            />
          </div>
        )
      }
    </div>
  );
};

const styles = {
  itemContainer: {
    margin: '20px 10px'
  },
  textInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& > input': {
      flexGrow: '2'
    }
  },
  Container: {
    boxSizing: 'border-box',
    border: '1px solid hsl(0, 0%, 90%)',
    padding: '10px 10px',
    margin: '20px',
    borderRadius: '5px',
    [DARK]: { borderColor: S.line }
  }
};

export default injectSheet(styles)(BackgroundPicker);
